import { useCallback, useState } from 'react'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { checkEmailRegex, validationMessages } from 'constants/validation'

import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import { CopyIcon } from 'shared/icons'

import { Section } from 'components/controls/section'
import { ImageFileInput } from 'components/controls/imageFileInput'
import { OptionSelect } from 'components/controls/optionSelect'

import { UserFormData, UserResult, UserRoles, useGetUserIcon } from 'interface/user'
import { GroupResult } from 'interface/user/group'

import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { convertToISODateTime } from 'shared/util'

const {
  Required,
  TooLongLength,
  TooShortLength,
  InvalidEMailAddress
} = validationMessages

const formSchema = yup.object().shape({
  userName: yup.string().required(Required)
    .min(3, TooShortLength(3))
    .max(20, TooLongLength(20)),
  email: yup.string().required(Required)
    .matches(checkEmailRegex, InvalidEMailAddress),
  role: yup.array()
    .of(
      yup.object().shape({
        label: yup.string().required(),
        key: yup.string().required(),
        isChecked: yup.boolean().required(),
      })
    )
    .required(Required)
    .test({
      name: '',
      message: Required,
      test: (value) => value?.some(({ isChecked }) => isChecked),
    }),
  group: yup.array()
    .min(1, Required)
    .required(Required),
  effectiveTime: yup
    .string()
    .nullable()
    .transform(value => (value === "" ? null : value))
})

type useUserFormProps = {
  groupList: GroupResult[],
  user?: UserResult,
  onSubmit: (body: { form: UserFormData, icon?: File }) => void
}
export const useUserForm = ({
  groupList,
  user,
  onSubmit
}: useUserFormProps) => {
  const iconState = useGetUserIcon(user?.userId)
  const [iconFile, setIconFile] = useState<File | undefined>(undefined)

  const form = useForm({
    defaultValues: user ? {
      userName: user.userName,
      email: user.email,
      role: UserRoles.map(role => ({
        label: role.label,
        key: role.key,
        isChecked: user.role.includes(role.key),
      })),
      group: user.group?.map((group) => group.groupId),
      effectiveTime: user.effectiveTime
    } : {
      userName: '',
      email: '',
      role: [],
      group: []
    },
    mode: 'onChange',
    criteriaMode: 'all',
		resolver: yupResolver(formSchema)
  })
  return {
    ...form,
    user,
    groupList,
    icon: {
			state: iconState,
			file: iconFile,
			set: setIconFile
		},
    submit: form.handleSubmit(input => onSubmit({
      form: {
        userName: input.userName,
        group: input.group,
        role: input.role.flatMap(role => role.isChecked ? role.key : []),
  			effectiveTime: convertToISODateTime(input.effectiveTime)
      },
      icon: iconFile
    }))
  }
}

export const UserForm = ({
  groupList,
  control,
  setValue,
  trigger,
  formState: { errors },
	icon,
  openResetPasswordConfirm
}: ReturnType<typeof useUserForm> & {
  openResetPasswordConfirm: () => void
}) => {
  const [isOpenTip, setIsOpenTip] = useState(false)
  const onClickCopyMailAddress = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text)
      .then(() => {
        setIsOpenTip(true)
        setTimeout(() => {
          setIsOpenTip(false)
        }, 2000)
      })
    },
    []
  )
  const handleOnClickCloseTip = useCallback(
    () => {
      setIsOpenTip(false)
    },
    []
  )
	const clearEffectiveTime = useCallback(
		() => {
			setValue("effectiveTime", null)
		},
		[setValue]
	)

  return (
		<Stack direction="row" spacing={2}>
			<Stack direction="column" spacing={1} width="200px" justifyContent="center">
				<ImageFileInput
					state={icon.state}
					onChangeFile={icon.set}
					emptyImage={<></>}
					alt="ユーザーアイコン"
				/>
			</Stack>

      <Stack direction="column">
        <Section label="ユーザー名" labelProps={{ py: 2 }}>
          <Controller
            control={control}
            name="userName"
            render={({ field }) => (
              <TextField
                {...field}
                variant="outlined"
                size="small"
                error={!!errors.userName}
                helperText={errors.userName ? errors.userName.message : ''}
              />
            )}
          />
        </Section>

        <Section label="メールアドレス" labelProps={{ py: 2 }}>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <TextField
                {...field}
                variant="outlined"
                size="small"
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ''}
                InputProps={{
                  endAdornment: (
                    <Tooltip
                      open={isOpenTip}
                      arrow
                      title="Copy!"
                      placement="top"
                      onClose={handleOnClickCloseTip}
                    >
                      <IconButton onClick={() => onClickCopyMailAddress(field.value)}>
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            )}
          />
        </Section>

        <Section label="パスワード" labelProps={{ py: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography component="div" variant="body1">
              ********
            </Typography>
            <Button
              variant="outlined"
              color="info"
              size="large"
              onClick={openResetPasswordConfirm}
            >
              リセット
            </Button>
          </Stack>
        </Section>

        <Section label="グループ" labelProps={{ py: 2 }}>
          <Controller
            control={control}
            name="group"
            render={({field}) => (
              <>
                <OptionSelect
                  {...field}
                  variant="outlined"
                  size="small"
                  optionList={(groupList ?? []).map((group) => ({
                    id: group.groupId,
                    label: group.groupName
                  }))}
                />
                {errors.group && (
                  <FormHelperText error>{errors.group.message}</FormHelperText>
                )}
              </>
            )}
          />  
        </Section>

        <Section label="権限" labelProps={{ py: 2 }}>
          <FormControl component="fieldset" error={!!errors.role}>
            <FormGroup>
              <Stack direction="row" flexWrap="wrap">
                {UserRoles.map((role, index) => (
                  <Controller
                    key={role.key}
                    control={control}
                    name={`role.${index}.isChecked`}
                    render={({ field }) => (
                      <FormControlLabel
                        key={`role-${index}`}
                        label={role.label}
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e)
                              trigger('role')
                            }}
                          />
                        }
                      />
                    )}
                  />
                ))}
              </Stack>
            </FormGroup>
            <FormHelperText error>{errors.role?.message}</FormHelperText>
          </FormControl>
        </Section>

        <Section label="有効期限" labelProps={{ py: 2 }} disablePaddingY>
          <Stack direction="row" spacing={1}>
            <Controller
              name="effectiveTime"
              control={control}
              rules={{
                required: false,
                validate: (value) => {
                  const formatDate = dayjs(value).format('YYYY/MM/DD HH:mm')
                  if (!dayjs(formatDate).isValid()) {
                    return '日時形式が間違っています'
                  }
                }
              }}
              render={({ field, formState: { errors } }) => (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="ja"
                >
                  <DesktopDatePicker
                    {...field}
                    format="YYYY/MM/DD HH:mm"
                    value={dayjs(field.value)}
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "outlined",
                        error: !!errors.effectiveTime,
                        helperText: errors.effectiveTime ? errors.effectiveTime.message : ''
                      }
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <Button
              size="small"
              onClick={clearEffectiveTime}
            >
              クリア
            </Button>
          </Stack>
        </Section>
      </Stack>
    </Stack>
  )
}
